import { useRef, useState } from "react";
import { CompanyData } from "../../pages";
import { RequestError, useApi } from "../../services";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid2, TextField, Typography } from "@mui/material";
import { formatBrazilianPhone, formatCNPJ } from "../../core";
import { FileUpload } from "../fileUpload";
import { HidroDXEditor } from "../edit";
import { SelectMunicipio } from "../estadoMunicipio";
import { ErrorOnDialog } from "./errorModal";

interface EditCompanieProps {
  open: boolean;
  showCNPJ?: boolean;
  showSede?: boolean;
  showPhone?: boolean;
  showMunicipio?: boolean;
  initialCompany?: CompanyData;
  onCompany?: (compay: CompanyData) => void;
  onCancel?: () => void;
}

export const EditCompanieModal: React.FC<EditCompanieProps> = ({
  open,
  showCNPJ,
  showSede,
  showPhone,
  showMunicipio,
  initialCompany,
  onCompany,
  onCancel,
}) => {
  const api = useApi();

  const diffDescricao = useRef<string>(initialCompany?.descricao ?? "");

  const [logo, setLogo] = useState<File>();
  const [banner, setBanner] = useState<File>();
  const [error, setError] = useState<RequestError>();
  const [company, setCompany] = useState<Partial<CompanyData>>(initialCompany ?? {
    id: "",
    nome: "",
    cnpj: "",
    sede: "",
    phone: "",
    descricao: "",
  });

  const handleConfirmEdit = async () => {
    if (!company) return;

    let result: CompanyData | RequestError;
    if ((company.id ?? "") === "") {
      result = await api.company.add((company as CompanyData), logo, banner);
    } else {
      result = await api.company.updateCompany(company.id ?? "", company, logo, banner);
    }

    if ("error" in result) {
      setError(result);
      return;
    }

    if (onCompany) onCompany(result ?? (company as CompanyData));
  };

  const handleCancelEdit = () => {
    if (onCancel) onCancel();
    setCompany({
      id: "",
      nome: "",
      cnpj: "",
      sede: "",
      phone: "",
      descricao: "",
    });
  };

  return <>

    {error && <ErrorOnDialog
      open={Boolean(error)}
      onClose={() => setError(undefined)}
      {...error}
    />}

    <Dialog
      fullWidth
      open={open}
      maxWidth={false}
      onClose={handleCancelEdit}
    >
      <DialogTitle id="alert-dialog-title">{initialCompany ? "Editar Empreendimento" : "Adicionar Empreendimento"}</DialogTitle>

      <DialogContent>
        <Divider />
        <div style={{ height: "1em" }} />

        <Box sx={{
          gap: "0.5em",
          display: "flex",
          flexDirection: "column",
        }}>
          {/* Textos e upload*/}
          <Box sx={{
            flex: 1,
            gap: "0.5em",
            display: "flex",
            flexDirection: "row",
          }}>
            <Grid2 container flex={1} spacing={"0.5em"}>
              <Grid2 size={showCNPJ ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={company.nome ?? ""}
                  onChange={(e) => setCompany({ ...company, nome: e.target.value })}
                />
              </Grid2>

              {showCNPJ && <Grid2 size={6}>
                <TextField
                  fullWidth
                  label="CNPJ"
                  value={formatCNPJ(company.cnpj ?? "")}
                  onChange={(e) => setCompany({ ...company, cnpj: e.target.value })}
                />
              </Grid2>}

              {showSede && <Grid2 size={12}>
                <TextField
                  fullWidth
                  label="Sede"
                  value={company.sede}
                  onChange={(e) => setCompany({ ...company, sede: e.target.value })}
                />
              </Grid2>}

              {showPhone && <Grid2 size={showSede ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Telefone"
                  value={formatBrazilianPhone(company.phone ?? "")}
                  onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                />
              </Grid2>}

              {showMunicipio && <Grid2 size={showMunicipio ? 6 : 12}>
                <SelectMunicipio
                  variant="contained"
                  municipio={company.municipio}
                  onChoose={(municipio) => setCompany({
                    ...company,
                    municipio: municipio.id,
                  })}
                />
              </Grid2>}
            </Grid2>

            {/* Upload de arquivos */}
            <FileUpload
              sx={{ height: "190px", width: "236px" }}
              text="Arraste um arquivo aqui ou clique para adicionar a LOGO"
              accept={{
                'image/jpeg': ['.jpeg', '.jpg'],
                'image/png': ['.png'],
                'image/webp': ['.webp'],
                'image/svg+xml': ['.svg']
              }}
              files={logo && [logo]}
              onAddFile={(files) => {
                if (files?.length) setLogo(files[0]);
              }}
            />
            <FileUpload
              sx={{ height: "190px", width: "236px" }}
              text="Arraste um arquivo aqui ou clique para adicionar o BANNER"
              accept={{
                'image/jpeg': ['.jpeg', '.jpg'],
                'image/png': ['.png'],
                'image/webp': ['.webp'],
                'image/svg+xml': ['.svg']
              }}
              files={banner && [banner]}
              onAddFile={(files) => {
                if (files?.length) setBanner(files[0]);
              }}
            />
          </Box>

          {/* Descrição */}
          <Typography>Descrição:</Typography>
          <div style={{ textAlign: "justify" }}>
            <HidroDXEditor
              image={false}
              chartMap={false}
              diffText={diffDescricao.current}
              startText={company.descricao}
              onChange={(text) => setCompany({ ...company, descricao: text })}
            />
          </div>
        </Box>
      </DialogContent>


      <DialogActions>
        <Button variant="contained" onClick={handleCancelEdit}>
          Cancelar
        </Button>
        <Button onClick={handleConfirmEdit} autoFocus>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog >
  </>;
};